import { Box, Button, Grid, InputAdornment, Stack, Typography } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormDropdown } from 'src/components/FormPart/FormDropdown';
import { FormNumberInput } from 'src/components/FormPart/FormNumberInput';
import { SelectOption } from 'src/UIKit';
import {
  UseCasesResponse,
  useGetUseCasesApi,
  useGetBrandRelationshipsApi,
  useGetVerticalsApi,
  useGetEntityTypesApi,
} from 'src/api/campaignRegistry';
import { CompanyPhoneType } from '../../api/CompanyPhoneApi';
import { useAdminCompaniesApi, useCompaniesApi } from '../../api';
import { processEnumOptions } from '../../helpers';
import { UseCaseSelector, SubUseCaseSelector } from 'src/components/UseCaseSelector';

export type PhoneStepData = {
  phoneNumberType: string;
  phoneNumber: string;
  useCase: string;
  subUseCases: string[];
  vertical?: string;
  brandRelationship?: string;
  entityType?: string;
};

interface AddPhoneStepProps {
  phoneStepData: PhoneStepData | null;
  defaultUseCase?: string;
  defaultSubUseCases?: string[];
  onBack: () => void;
  onSubmit: (data: PhoneStepData) => void;
}

const phoneNumberTypeOptions: SelectOption<string, string>[] = [
  { value: CompanyPhoneType.toll_free, title: 'Toll Free' },
  { value: CompanyPhoneType._10dlc_local, title: '10DLC New' },
  { value: CompanyPhoneType._10dlc_hosted, title: '10DLC Hosted' },
];

export const AddPhoneStep: FC<AddPhoneStepProps> = ({
  phoneStepData,
  defaultUseCase,
  defaultSubUseCases,
  onBack,
  onSubmit,
}) => {
  const form = useForm<PhoneStepData>({
    defaultValues: phoneStepData || {
      phoneNumberType: undefined,
      phoneNumber: undefined,
      useCase: defaultUseCase || '',
      subUseCases: defaultSubUseCases || [],
      vertical: undefined,
      brandRelationship: undefined,
      entityType: undefined,
    },
    mode: 'onChange',
  });

  const [useCasesData, setUseCasesData] = useState<UseCasesResponse>({});
  const [brandRelationships, setBrandRelationships] = useState<SelectOption[]>([]);
  const [verticals, setVerticals] = useState<SelectOption[]>([]);
  const [entityTypes, setEntityTypes] = useState<SelectOption[]>([]);
  const [phoneNumberError, setPhoneNumberError] = useState<string | undefined>(undefined);
  const [isValidatingPhone, setIsValidatingPhone] = useState(false);
  const [eligibilityStatus, setEligibilityStatus] = useState<
    'unchecked' | 'eligible' | 'ineligible' | 'error'
  >('unchecked');
  const [eligibilityError, setEligibilityError] = useState<string | undefined>(undefined);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);

  const getUseCases = useGetUseCasesApi();
  const getBrandRelationships = useGetBrandRelationshipsApi();
  const getVerticals = useGetVerticalsApi();
  const getEntityTypes = useGetEntityTypesApi();
  const { checkEligibility: checkEligibilityApi } = useAdminCompaniesApi();
  const companiesApi = useCompaniesApi();

  const { phoneNumberType, phoneNumber } = form.watch();

  const is10DLC =
    phoneNumberType === CompanyPhoneType._10dlc_local ||
    phoneNumberType === CompanyPhoneType._10dlc_hosted;

  const phoneStr = String(phoneNumber || '');
  const isPhoneComplete = phoneStr.length === 10;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [useCasesResponse, brandRelResponse, verticalsResponse, entityTypesResponse] =
          await Promise.all([
            getUseCases(),
            getBrandRelationships(),
            getVerticals(),
            getEntityTypes(),
          ]);

        setUseCasesData(useCasesResponse.data || {});
        setBrandRelationships(
          processEnumOptions(brandRelResponse.data).map(o => ({ value: o.value, title: o.label })),
        );
        setVerticals(
          processEnumOptions(verticalsResponse.data).map(o => ({ value: o.value, title: o.label })),
        );
        setEntityTypes(
          processEnumOptions(entityTypesResponse.data).map(o => ({
            value: o.value,
            title: o.label,
          })),
        );
      } catch (error) {
        console.error('Failed to fetch form data:', error);
      }
    };
    fetchData();
  }, [getUseCases, getBrandRelationships, getVerticals, getEntityTypes]);

  useEffect(() => {
    setEligibilityStatus('unchecked');
    setEligibilityError(undefined);
  }, [phoneNumber, phoneNumberType]);

  const validatePhoneNumber = async (phone: string | undefined): Promise<string | true> => {
    setPhoneNumberError(undefined);
    form.clearErrors('phoneNumber');

    const value = String(phone || '');
    if (value.length !== 10) {
      return true;
    }

    setIsValidatingPhone(true);
    try {
      const response = await companiesApi.getAdminCompanies({
        limit: 1,
        offset: 0,
        phone: '+1' + value,
      });

      if (response.data.items.length > 0) {
        const company = response.data.items[0];
        const errorMessage = `This phone number is already used by company "${company.name}"`;
        setPhoneNumberError(errorMessage);
        form.setError('phoneNumber', { type: 'manual', message: errorMessage });
        return errorMessage;
      }

      return true;
    } catch (error) {
      console.error('Error validating phone number:', error);
      return true;
    } finally {
      setIsValidatingPhone(false);
    }
  };

  const handleCheckEligibility = async () => {
    if (!isPhoneComplete) return;

    setIsCheckingEligibility(true);
    setEligibilityError(undefined);
    try {
      const response = await checkEligibilityApi(`+1${phoneStr}`);
      if (response.data.isEligible) {
        setEligibilityStatus('eligible');
      } else {
        setEligibilityStatus('ineligible');
        setEligibilityError('This phone number is not eligible for 10DLC Hosted.');
      }
    } catch {
      setEligibilityStatus('error');
      setEligibilityError('Unable to verify phone number eligibility. Please try again.');
    } finally {
      setIsCheckingEligibility(false);
    }
  };

  const getSubUseCasesValidationMessage = () => {
    const formData = form.getValues();
    const useCaseDetails = useCasesData[formData.useCase];
    const minSubUsecases = useCaseDetails?.minSubUsecases || 0;
    const currentSubUsecases = formData.subUseCases || [];

    if (minSubUsecases > 0 && currentSubUsecases.length < minSubUsecases) {
      return `Please select at least ${minSubUsecases} sub-use case${minSubUsecases > 1 ? 's' : ''}. Currently selected: ${currentSubUsecases.length}`;
    }
    return undefined;
  };

  const isFormValid = () => {
    if (!form.formState.isValid || !isPhoneComplete || phoneNumberError) return false;
    if (phoneNumberType === CompanyPhoneType._10dlc_hosted && eligibilityStatus !== 'eligible')
      return false;
    if (is10DLC && getSubUseCasesValidationMessage()) return false;
    return true;
  };

  const handleNext = () => {
    const formData = form.getValues();
    if (!is10DLC) {
      formData.useCase = '';
      formData.subUseCases = [];
      formData.brandRelationship = undefined;
      formData.vertical = undefined;
      formData.entityType = undefined;
    }
    onSubmit(formData);
  };

  const showEligibilityButton =
    phoneNumberType === CompanyPhoneType._10dlc_hosted && isPhoneComplete && !phoneNumberError;

  const subUseCasesMessage = getSubUseCasesValidationMessage();

  return (
    <Grid container spacing={1} mt={1}>
      <Grid item xs={12} sm={6}>
        <FormDropdown
          form={form}
          fieldName="phoneNumberType"
          label="Phone Number Type"
          placeholder="Select Phone Number Type"
          options={phoneNumberTypeOptions}
          validation={{ required: 'Phone Number Type is required' }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormNumberInput
          form={form}
          format={{ style: 'phoneNumber' }}
          max={9999999999}
          fieldName="phoneNumber"
          label="Phone Number"
          placeholder="Enter Phone Number"
          helperText={phoneNumberError}
          validation={{
            required: 'Phone Number is required',
            validate: validatePhoneNumber,
          }}
          inputProps={{
            startAdornment: <InputAdornment position="start">+1</InputAdornment>,
          }}
        />
      </Grid>

      {showEligibilityButton && (
        <Grid item xs={12}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Button
              type="button"
              onClick={handleCheckEligibility}
              disabled={isCheckingEligibility}
              variant="outlined"
              color="secondary"
              size="small"
            >
              {isCheckingEligibility ? 'Checking...' : 'Check Eligibility'}
            </Button>
            {eligibilityStatus === 'eligible' && (
              <Typography variant="body2" color="success.main">
                Eligible for 10DLC Hosted
              </Typography>
            )}
            {(eligibilityStatus === 'ineligible' || eligibilityStatus === 'error') && (
              <Typography variant="body2" color="error">
                {eligibilityError}
              </Typography>
            )}
          </Stack>
        </Grid>
      )}

      {is10DLC && (
        <>
          <Grid item xs={12} sm={6}>
            <FormDropdown
              form={form}
              fieldName="brandRelationship"
              label="Brand Relationship"
              placeholder="Select Brand Relationship"
              options={brandRelationships}
              validation={{ required: 'Brand Relationship is required for 10DLC numbers' }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormDropdown
              form={form}
              fieldName="vertical"
              label="Vertical"
              placeholder="Select Vertical"
              options={verticals}
              validation={{ required: 'Vertical is required for 10DLC numbers' }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormDropdown
              form={form}
              fieldName="entityType"
              label="Entity Type"
              placeholder="Select Entity Type"
              options={entityTypes}
              validation={{ required: 'Entity Type is required for 10DLC numbers' }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box mt={0.75}>
              <UseCaseSelector
                form={form}
                label="Use Case"
                useCaseField="useCase"
                useCasesData={useCasesData}
                validation={{ required: 'Use Case is required for 10DLC numbers' }}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <SubUseCaseSelector form={form} useCasesData={useCasesData} />
          </Grid>
        </>
      )}

      {subUseCasesMessage && (
        <Grid item xs={12}>
          <Typography variant="body2" color="error" textAlign="right" mt={1}>
            {subUseCasesMessage}
          </Typography>
        </Grid>
      )}

      <Grid item xs={12} display="flex" justifyContent="space-between">
        <Button type="button" onClick={onBack} variant="outlined" color="secondary" size="large">
          Back
        </Button>
        <Button
          type="button"
          onClick={handleNext}
          disabled={!isFormValid() || isValidatingPhone}
          variant="contained"
          color="secondary"
          size="large"
        >
          Next
        </Button>
      </Grid>
    </Grid>
  );
};
